import type { CategoriaDeStatus } from '@/features/tickets/types';
import type {
  Desfecho,
  JanelaDeMudanca,
  NivelDeRisco,
  TipoDeMudanca,
} from '@/features/changes/tipos';

/**
 * O formato do que a API de Release Management devolve.
 *
 * "Versão de entrega" é o termo da interface (documento 09 §6); `Release` é o
 * nome da tabela. Os dois convivem: o tipo fala a língua do servidor, a tela
 * fala a língua do ITIL em português.
 *
 * Nada aqui é inventado: cada campo existe em alguma resposta de
 * `apps/api/src/itsm/releases/releases.controller.ts`.
 */

export const TIPOS_DE_RELEASE = ['MAJOR', 'MINOR', 'EMERGENCY', 'PACKAGE'] as const;
export type TipoDeRelease = (typeof TIPOS_DE_RELEASE)[number];

export const ROTULO_DE_TIPO_DE_RELEASE: Record<TipoDeRelease, string> = {
  MAJOR: 'Maior',
  MINOR: 'Menor',
  EMERGENCY: 'Emergencial',
  PACKAGE: 'Pacote',
};

/**
 * As oito situações do ciclo.
 *
 * `ROLLED_BACK` e `CANCELLED` são terminais e não se alcançam por
 * `POST /releases/:id/status`: reverter é rota própria, porque marca cada
 * mudança do conjunto e exige o motivo; cancelar também é rota própria.
 */
export const SITUACOES_DE_RELEASE = [
  'PLANNING',
  'BUILD',
  'TESTING',
  'READY',
  'DEPLOYING',
  'DEPLOYED',
  'ROLLED_BACK',
  'CANCELLED',
] as const;
export type SituacaoDeRelease = (typeof SITUACOES_DE_RELEASE)[number];

export const ROTULO_DE_SITUACAO: Record<SituacaoDeRelease, string> = {
  PLANNING: 'Planejamento',
  BUILD: 'Construção',
  TESTING: 'Teste',
  READY: 'Pronta',
  DEPLOYING: 'Implantando',
  DEPLOYED: 'Implantada',
  ROLLED_BACK: 'Revertida',
  CANCELLED: 'Cancelada',
};

export type DesfechoDeRelease = 'SUCCESSFUL' | 'SUCCESSFUL_WITH_ISSUES' | 'FAILED' | 'ROLLED_BACK';

/**
 * A linha de `Release` como o banco a guarda.
 *
 * É o que `POST /releases`, `POST …/status` e `POST …/cancel` devolvem: a linha
 * crua, sem contagem e sem relacionamento. Só `GET /releases` acrescenta
 * `_count` — ver `ReleaseListada`.
 */
export interface LinhaDeRelease {
  id: string;
  code: string;
  name: string;
  description: string | null;
  type: TipoDeRelease;
  status: SituacaoDeRelease;
  ownerId: string;
  plannedDeployAt: string | null;
  actualDeployAt: string | null;
  changeWindowId: string | null;
  deploymentPlan: string | null;
  verificationPlan: string | null;
  rollbackPlan: string | null;
  outcome: DesfechoDeRelease | null;
  rollbackReason: string | null;
  rolledBackAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** `GET /releases` — a linha com a contagem do que ela agrupa. */
export interface ReleaseListada extends LinhaDeRelease {
  _count: { changes: number; cis: number };
}

/** Uma mudança dentro da versão de entrega. */
export interface MembroDaRelease {
  releaseId: string;
  ticketId: string;
  sequence: number;
  addedById: string | null;
  addedAt: string;
  ticket: {
    id: string;
    number: string;
    subject: string;
    statusCategory: CategoriaDeStatus;
    /**
     * ★ O desfecho de cada mudança, que é o que torna a reversão do conjunto
     * visível: depois de `POST /releases/:id/rollback`, cada uma destas linhas
     * aparece com `ROLLED_BACK`.
     */
    changeDetail: {
      changeType: TipoDeMudanca;
      riskLevel: NivelDeRisco;
      outcome: Desfecho | null;
    } | null;
  };
}

/** A linha crua de `ReleaseCi`, como `POST /releases/:id/cis` a devolve. */
export interface LinhaDeIcDaRelease {
  releaseId: string;
  ciId: string;
  versionBefore: string | null;
  versionAfter: string | null;
}

/** A mesma linha com o item de configuração junto, como `GET /releases/:id`. */
export interface IcDaRelease extends LinhaDeIcDaRelease {
  ci: { id: string; name: string; ciType: string };
}

/** `GET /releases/:id` — a versão de entrega inteira. */
export interface Release extends LinhaDeRelease {
  changes: MembroDaRelease[];
  cis: IcDaRelease[];
  changeWindow: JanelaDeMudanca | null;
}

/** O que `POST /releases/:id/rollback` devolve. */
export interface ResultadoDaReversao {
  release: LinhaDeRelease;
  /** Os `ticketId` das mudanças que **esta** reversão marcou. */
  changesMarked: string[];
}
